import { Component, Inject, OnInit, Optional } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { Register, ContractInviteResponse } from "../models/user";
import { PortalService } from "../services/portal-service.service";
import { LogService } from "../services/log.service";
import { AuthService } from "../services/auth.service";
import { matchingInputsValidator } from "../validators/matchingInputs";

@Component({
  standalone: false,
  selector: "app-register",
  templateUrl: "./register.component.html",
  styleUrls: ["./register.component.scss"],
})
export class RegisterComponent implements OnInit {
  RegisterForm: UntypedFormGroup;
  error: any = null;
  success = false;
  registering = false;
  accepting = false;
  inviteToken: string = null;
  invite: ContractInviteResponse = null;
  constructor(
    private fb: UntypedFormBuilder,
    private portalService: PortalService,
    private logService: LogService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    @Optional() @Inject(MAT_DIALOG_DATA) private data?: { inviteToken?: string; invite?: ContractInviteResponse },
    @Optional() private dialogRef?: MatDialogRef<RegisterComponent>,
  ) {}

  async ngOnInit() {
    this.inviteToken = this.data?.inviteToken || this.route.snapshot.queryParamMap.get("invite");
    if (this.inviteToken && !this.openedAsDialog) {
      await this.router.navigate(["/login"], { queryParams: { invite: this.inviteToken } });
      return;
    }
    this.RegisterForm = this.fb.group(
      {
        userName: ["", [Validators.required]],
        email: ["", [Validators.required, Validators.email]],
        password: ["", [Validators.required]],
        confirm: ["", [Validators.required]],
        resetQuestion: ["", [Validators.required]],
        resetAnswer: ["", [Validators.required]],
      },
      { validator: matchingInputsValidator("password", "confirm") },
    );

    if (!this.inviteToken) {
      this.RegisterForm.addControl("contractPassword", this.fb.control("", Validators.required));
      this.RegisterForm.addControl("contractNo", this.fb.control(1, Validators.required));
    }

    if (this.inviteToken) {
      try {
        this.invite = this.data?.invite || await this.portalService.getContractInvite(this.inviteToken);
        this.f.email.setValue(this.invite.email);
        this.f.email.disable();
        if (this.authService.isLoggedIn()) {
          await this.acceptInvite();
        }
      } catch (error) {
        this.error = error;
      }
    }
  }

  async registerUser() {
    if (this.registerDisabled()) return false;
    this.resetMessage();
    this.registering = true;
    const registration = new Register();
    registration.username = this.f.userName.value;
    registration.password = this.f.password.value;
    registration.email = this.invite ? this.invite.email : this.f.email.value;
    registration.resetQuestion = this.f.resetQuestion.value;
    registration.resetAnswer = this.f.resetAnswer.value;
    registration.inviteToken = this.inviteToken;
    if (!this.inviteToken) {
      registration.contractNo = this.f.contractNo.value;
      registration.contractPassword = this.f.contractPassword.value;
    }
    try {
      await this.portalService.registerUser(registration);
      this.success = true;
    } catch (error) {
      this.error = error;
    } finally {
      this.registering = false;
    }
  }

  get f() {
    return this.RegisterForm.controls;
  }

  resetMessage() {
    this.success = false;
    this.error = null;
  }

  registerDisabled(): boolean {
    return this.registering || !this.RegisterForm || this.RegisterForm.invalid ||
      (!!this.inviteToken && (!this.invite || this.invite.existingUser));
  }

  get openedAsDialog(): boolean {
    return !!this.dialogRef;
  }

  async acceptInvite() {
    this.resetMessage();
    if (this.accepting || !this.inviteToken) return false;
    this.accepting = true;
    try {
      await this.portalService.acceptContractInvite(this.inviteToken);
      this.success = true;
    } catch (error) {
      this.error = error;
    } finally {
      this.accepting = false;
    }
  }

  async loginForInvite() {
    await this.router.navigate(["/login"], { queryParams: { invite: this.inviteToken } });
  }

  close(): void {
    if (this.dialogRef) {
      this.dialogRef.close(this.success);
      return;
    }
    this.router.navigate(["/login"]);
  }
}
